'use client';

import { useState, useEffect, useRef } from 'react';
import type { NavigationItem } from '../navigationTypes';
import type { ResponsiveFlyoutHeaderProps } from './ResponsiveFlyoutHeader.client';
import { ResponsiveFlyoutHeader } from './ResponsiveFlyoutHeader.client';
import { getNavigationItemKey } from '../getNavigationItemKey';
import { ResponsiveFlyoutTransition } from './ResponsiveFlyoutTransition.client';
import { ResponsiveFlyoutContent } from './ResponsiveFlyoutContent.client';
import type { TreeNavigationDirection } from './useTreeNavigationPopup';

export type ResponsiveFlyoutProps = {
  item?: NavigationItem;
  direction: TreeNavigationDirection;
  onItemClick?: (item: NavigationItem) => void;
  onNavigate?: (item: NavigationItem) => void;
} & ResponsiveFlyoutHeaderProps;

/**
 * Navigation popup shown to the user when they interact with the main navigation, displaying child navigation items in the nav tree.
 */

export const ResponsiveFlyout = ({
  item,
  direction,
  onClose,
  onBack,
  onItemClick,
  onNavigate,
  shouldFocusCloseButton,
}: ResponsiveFlyoutProps) => {
  const [newItem, setItem] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (newItem) {
      buttonRef.current?.focus();
      setItem(false);
    }
  }, [newItem]);

  const transitionKey = item && getNavigationItemKey(item);

  return (
    <div className="bg-color-background-base lg:bg-color-grey-light flex w-full flex-1 flex-col pb-4">
      <div className="flex flex-1 flex-col gap-2">
        <ResponsiveFlyoutHeader
          shouldFocusCloseButton={shouldFocusCloseButton}
          ref={buttonRef}
          title={item?.displayName}
          onBack={onBack}
          onClose={onClose}
        />
        <ResponsiveFlyoutTransition direction={direction} transitionKey={transitionKey}>
          {(ref) => (
            <ResponsiveFlyoutContent
              ref={ref}
              item={item}
              onItem={setItem}
              onItemClick={onItemClick}
              onNavigate={onNavigate}
            />
          )}
        </ResponsiveFlyoutTransition>
      </div>
    </div>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default ResponsiveFlyout;
